import React, { useState } from 'react';
import { TrendingUp, TrendingDown, RefreshCw, Calendar, DollarSign, AlertCircle } from 'lucide-react';
import Sidebar, { useSidebar } from '../../components/ui/Sidebar';
import Button from '../../components/ui/Button';
import TopNavbar from '../../components/ui/TopNavbar';

const ExchangeRates = () => {
  const { isCollapsed } = useSidebar();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(new Date()?.toLocaleString());

  // Mock exchange rate data
  const [currentRates, setCurrentRates] = useState([
    {
      id: 1,
      fromCurrency: 'AUD',
      toCurrency: 'USD',
      rate: 0.6425,
      change: +0.0012,
      changePercent: +0.19,
      lastUpdated: '2025-01-13 17:45:00',
      high24h: 0.6445,
      low24h: 0.6398
    },
    {
      id: 2,
      fromCurrency: 'USD',
      toCurrency: 'AUD',
      rate: 1.5564,
      change: -0.0029,
      changePercent: -0.19,
      lastUpdated: '2025-01-13 17:45:00',
      high24h: 1.5625,
      low24h: 1.5498
    },
  ]);

  // Historical rates
  const historicalRates = [
    { date: '2025-01-13', audToUsd: 0.6425, usdToAud: 1.5564 },
    { date: '2025-01-12', audToUsd: 0.6413, usdToAud: 1.5593 },
    { date: '2025-01-11', audToUsd: 0.6398, usdToAud: 1.5630 },
    { date: '2025-01-10', audToUsd: 0.6405, usdToAud: 1.5613 },
    { date: '2025-01-09', audToUsd: 0.6418, usdToAud: 1.5581 },
    { date: '2025-01-08', audToUsd: 0.6432, usdToAud: 1.5547 },
    { date: '2025-01-07', audToUsd: 0.6445, usdToAud: 1.5516 },
  ];

  // Quick conversions
  const quickConversions = [
    { amount: 1000, from: 'AUD', to: 'USD', result: 642.50 },
    { amount: 1000, from: 'USD', to: 'AUD', result: 1556.40 },
    { amount: 50000, from: 'AUD', to: 'USD', result: 32125.00 },
    { amount: 50000, from: 'USD', to: 'AUD', result: 77820.00 },
  ];

  const [showAddRateModal, setShowAddRateModal] = useState(false);
  const [showEditRateModal, setShowEditRateModal] = useState(false);

  const handleRefreshRates = () => {
    setIsRefreshing(true);
    console.log('Refreshing exchange rates');
    // TODO: Implement API call to fetch latest rates
    setTimeout(() => {
      setIsRefreshing(false);
      alert('✅ Exchange rates refreshed!');
    }, 1500);
  };

  const handleAddCustomRate = () => {
    setShowAddRateModal(true);
  };

  const handleSaveCustomRate = (rateData) => {
    console.log('Saving custom rate:', rateData);
    // TODO: Implement custom rate saving
    alert('✅ Custom exchange rate added!');
    setShowAddRateModal(false);
  };

  const handleEditRate = (rateId) => {
    console.log('Editing rate:', rateId);
    setShowEditRateModal(true);
    // TODO: Load rate data for editing
  };

  const handleUpdateRate = (rateData) => {
    console.log('Updating rate:', rateData);
    // TODO: Implement rate update
    alert('✅ Exchange rate updated!');
    setShowEditRateModal(false);
  };

  const handleDeleteRate = (rateId) => {
    if (confirm('Are you sure you want to delete this exchange rate?')) {
      console.log('Deleting rate:', rateId);
      // TODO: Implement deletion
      alert('✅ Exchange rate deleted!');
    }
  };

  const handleExportRates = () => {
    console.log('Exporting exchange rates');
    // TODO: Implement export
    alert('✅ Exporting exchange rate history...');
  };

  const handleSetDefaultRate = (rateId) => {
    console.log('Setting default rate:', rateId);
    // TODO: Implement default setting
    alert('✅ Default exchange rate set!');
  };

  return (
    <div className="flex flex-col h-screen bg-background">
      <TopNavbar />
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-6 mt-[65px]">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Multi-Currency Exchange Rates</h1>
              <p className="text-gray-600 mt-1 text-sm sm:text-base">Track AUD/USD exchange rates for cost and fee calculations</p>
              <p className="text-xs text-gray-500 mt-1">Last refreshed: {lastRefreshed}</p>
            </div>
            <div className="flex gap-3 w-full sm:w-auto">
              <Button 
                onClick={handleRefreshRates}
                disabled={isRefreshing}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed w-full sm:w-auto justify-center"
              >
                <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                {isRefreshing ? 'Refreshing...' : 'Refresh Rates'}
              </Button>
            </div>
          </div>

          {/* Current Rates Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {currentRates?.map(rate => (
              <div key={rate?.id} className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 sm:p-6 border border-blue-200">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-2xl font-bold text-blue-900">
                        {rate?.fromCurrency}/{rate?.toCurrency}
                      </span>
                      {rate?.change > 0 ? (
                        <TrendingUp className="h-5 w-5 text-green-600" />
                      ) : (
                        <TrendingDown className="h-5 w-5 text-red-600" />
                      )}
                    </div>
                    <p className="text-sm text-blue-700">Current Exchange Rate</p>
                  </div>
                  <div className="bg-blue-600 rounded-lg p-3">
                    <DollarSign className="h-6 w-6 text-white" />
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <p className="text-4xl font-bold text-blue-900">{rate?.rate?.toFixed(4)}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-sm font-medium ${rate?.change > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {rate?.change > 0 ? '+' : ''}{rate?.change?.toFixed(4)} ({rate?.changePercent > 0 ? '+' : ''}{rate?.changePercent?.toFixed(2)}%)
                      </span>
                      <span className="text-sm text-blue-600">24h change</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 pt-3 border-t border-blue-300">
                    <div>
                      <p className="text-xs text-blue-700">24h High</p>
                      <p className="text-lg font-semibold text-blue-900">{rate?.high24h?.toFixed(4)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-blue-700">24h Low</p>
                      <p className="text-lg font-semibold text-blue-900">{rate?.low24h?.toFixed(4)}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-xs text-blue-700 pt-2">
                    <Calendar className="h-3 w-3" />
                    Last updated: {rate?.lastUpdated}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-auto p-4 sm:p-6 lg:p-8">
          <div className="grid grid-cols-2 gap-6">
            {/* Historical Rates */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Historical Rates (7 Days)</h2>
              </div>
              <div className="p-6">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left text-sm font-semibold text-gray-700 pb-3">Date</th>
                      <th className="text-right text-sm font-semibold text-gray-700 pb-3">AUD/USD</th>
                      <th className="text-right text-sm font-semibold text-gray-700 pb-3">USD/AUD</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historicalRates?.map((rate, index) => (
                      <tr key={index} className="border-b border-gray-100 last:border-b-0">
                        <td className="py-3 text-sm text-gray-600">
                          {new Date(rate.date)?.toLocaleDateString()}
                        </td>
                        <td className="py-3 text-sm text-right font-medium text-gray-900">
                          {rate?.audToUsd?.toFixed(4)}
                        </td>
                        <td className="py-3 text-sm text-right font-medium text-gray-900">
                          {rate?.usdToAud?.toFixed(4)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Quick Conversions */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Quick Conversions</h2>
              </div>
              <div className="p-6 space-y-4">
                {quickConversions?.map((conversion, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-2xl font-bold text-gray-900">
                          {conversion?.amount?.toLocaleString()} {conversion?.from}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">converts to</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-blue-600">
                          {conversion?.result?.toLocaleString()} {conversion?.to}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                          @ {conversion?.from === 'AUD' ? currentRates?.[0]?.rate?.toFixed(4) : currentRates?.[1]?.rate?.toFixed(4)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Rate Impact Notice */}
          <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-yellow-900 mb-1">Exchange Rate Impact</h3>
                <p className="text-sm text-yellow-800">
                  All AUD costs are automatically converted to USD for RNR fee calculations using the daily exchange rate.
                  Fee rates are quoted in USD per tonne and remain fixed regardless of exchange rate fluctuations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExchangeRates;